<template>
    <LMap :center="coordinates" :zoom="zoom" class="flex-grow-1" @ready="onMapReady" ref="container">
        <MapyCzOrthoPhotoLayer/>
        <LControl position="bottomleft">
            <div>
                <a :href="`https://mapy.cz/letecka?x=${longitude}&y=${latitude}&z=${zoom}`" target="_blank">
                    <img src="https://api.mapy.cz/img/api/logo.svg" />
                </a>
            </div>
        </LControl>
        <LMarker :lat-lng="coordinates" :icon="markerIcon"/>
    </LMap>
</template>

<script lang="ts">
import MapyCzOrthoPhotoLayer from '@/components/maps/MapyCzOrtophotoLayer.vue';
import L from 'leaflet';
import {computed, defineComponent, markRaw, nextTick, onBeforeUnmount, ref} from 'vue';
import {LControl, LMap, LMarker} from '@vue-leaflet/vue-leaflet';

export default defineComponent({
    components: {MapyCzOrthoPhotoLayer, LMap, LMarker, LControl},
    props: {
        longitude: {
            type: Number,
            required: true,
        },
        latitude: {
            type: Number,
            required: true,
        },
    },
    setup(props) {
        const container = ref<any>(null);
        const leafletMap = ref<L.Map | null>(null);
        const zoom = 18;
        const maxPopupHeight = ref<number | null>(null);
        let visibilityObserver: IntersectionObserver | null = null;

        const coordinates = computed(() => [props.latitude, props.longitude]);

        const markerIcon = markRaw(L.icon({
            iconUrl: require('leaflet/dist/images/marker-icon.png'),
            iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
            shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
            iconSize: [25, 41],
            iconAnchor: [12, 41],
            popupAnchor: [1, -34],
            shadowSize: [41, 41],
        }));

        function updateHeight(): void {
            const el = container.value as unknown as Element;
            maxPopupHeight.value = el.clientHeight * 0.8;
        }

        function onMapReady(mapInstance: L.Map): void {
            leafletMap.value = mapInstance;
            updateHeight();
            waitForVisibility();
        }

        function waitForVisibility(): void {
            const element = leafletMap.value!.getContainer();

            visibilityObserver = new IntersectionObserver((entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        nextTick(() => {
                            leafletMap.value?.invalidateSize();
                        });

                        if (visibilityObserver) {
                            visibilityObserver.disconnect();
                            visibilityObserver = null;
                        }
                    }
                });
            }, {threshold: 0.1});

            visibilityObserver.observe(element);
        }

        onBeforeUnmount(() => {
            visibilityObserver?.disconnect();
        });

        return {container, zoom, maxPopupHeight, coordinates, markerIcon, onMapReady};
    },
});
</script>
